<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  import { Checkbox, InlineNotification, Modal, TextInput } from 'carbon-components-svelte';
  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';

  import { deleteSLATask } from '../../../services';
  import { getServerErrorMessage } from '../../../utils';

  export let slaTaskName = '',
    slaTaskId: string,
    open = false;

  let loading = false,
    confirm = false,
    resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null,
    taskNameToDelete = '';

  const dispatch = createEventDispatcher<{
    close: { action: 'delete-task'; data: { id: string; friendlyName: string } } | undefined;
  }>();

  async function onSubmit(): Promise<void> {
    loading = true;
    resultNotification = null;

    try {
      await deleteSLATask(slaTaskId);
      dispatch('close', { action: 'delete-task', data: { id: slaTaskId, friendlyName: slaTaskName } });
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Please try again later.';
      console.error('[DeleteSlaTaskModal] Failed to delete SLA task!', errorMessage);

      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to delete SLA task! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function onModalClose() {
    dispatch('close');
  }
</script>

<Modal
  danger
  modalHeading="Delete SLA Task"
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  primaryButtonDisabled={!confirm || slaTaskName !== taskNameToDelete}
  preventCloseOnClickOutside={true}
  bind:open
  on:click:button--secondary={onModalClose}
  on:close={onModalClose}
  on:submit={onSubmit}
>
  <LoadingSpinner {loading}>
    {#if resultNotification}
      <InlineNotification
        hideCloseButton
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
      />
    {/if}

    <p class="locked">You are about to delete the SLA task <strong>{slaTaskName}</strong>.</p>
    <strong>All task schedule metadata and related associations will be removed.</strong>
    <p class="confirm">Are you sure?</p>

    <Checkbox
      labelText="I understand and I would like to proceed with SLA task deletion"
      bind:checked={confirm}
      disabled={slaTaskName === taskNameToDelete}
    />

    {#if confirm}
      <TextInput size="sm" placeholder="Enter the task name to proceed with deletion" bind:value={taskNameToDelete} />
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  .locked {
    user-select: none;
  }

  .confirm {
    padding: 1rem 0;
  }
</style>
